/**
 * Install-Prompt fuer PWA
 *
 * - Auf Android/Chrome wird der `beforeinstallprompt`-Event abgefangen und
 *   in einem dezenten Banner unten angeboten.
 * - Auf iOS-Safari gibt es das Event nicht — stattdessen zeigen wir einmal
 *   einen Hinweis mit Anleitung („Teilen → Zum Home-Bildschirm").
 * - Wenn der Nutzer abweist, merken wir uns das in localStorage und nerven
 *   nicht mehr fuer 30 Tage.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Download, Share, X } from 'lucide-react';

const DISMISS_KEY = 'teamhub.pwa-install.dismissed-until';
const SNOOZE_DAYS = 30;

const isInStandaloneMode = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS Safari
  window.navigator.standalone === true;

const isIosSafari = () => {
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
};

const dismissedRecently = () => {
  const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
  return until > Date.now();
};

const dismiss = () => {
  localStorage.setItem(DISMISS_KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
};

export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode() || dismissedRecently()) return;

    if (isIosSafari()) {
      // 5 s warten, damit der Nutzer erstmal ankommt
      const t = setTimeout(() => setShowIosHint(true), 5000);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'dismissed') dismiss();
    setDeferred(null);
  };

  const handleClose = () => {
    dismiss();
    setDeferred(null);
    setShowIosHint(false);
  };

  if (!deferred && !showIosHint) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 bg-card border border-border rounded-2xl shadow-xl p-4 flex items-start gap-3 animate-slide-up"
      role="dialog"
      aria-label="App installieren"
      data-testid="pwa-install-prompt"
    >
      <div className="rounded-xl bg-primary/10 p-2 shrink-0">
        {showIosHint ? <Share className="h-5 w-5 text-primary" /> : <Download className="h-5 w-5 text-primary" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">TeamHub als App installieren</p>
        {showIosHint ? (
          <p className="text-xs text-muted-foreground mt-1">
            Tippe auf <Share className="inline h-3 w-3 mx-0.5" /> Teilen und dann „Zum Home-Bildschirm hinzufügen".
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">
            Eigenes Icon auf dem Startbildschirm, Vollbild ohne Browserleiste, Push-Benachrichtigungen auch wenn der Tab zu ist.
          </p>
        )}
        {!showIosHint && (
          <Button
            size="sm"
            onClick={handleInstall}
            className="mt-2 h-7 rounded-full"
            data-testid="pwa-install-btn"
          >
            Installieren
          </Button>
        )}
      </div>
      <button
        type="button"
        onClick={handleClose}
        className="text-muted-foreground hover:text-foreground p-1 -m-1 shrink-0"
        aria-label="Schließen"
        data-testid="pwa-install-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
